import { Component } from 'react';
import { ErrorBoundaryFallback } from './ErrorBoundaryFallback';
import { topFrames } from './RouteErrorFallback';
import type { ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  /** Optional custom fallback. Receives the caught error and a reset function. */
  fallback?: (error: Error, reset: () => void) => ReactNode;
  /**
   * Change this to clear a caught error and retry the children.
   *
   * Callers pass the current pathname. An error boundary has no notion of
   * navigation — it holds the error in state until something clears it — so
   * once one route threw, EVERY subsequent route rendered the fallback: the
   * sidebar still worked, each click appeared to do nothing, and the only way
   * out was a manual reload. Keying the reset on location makes navigating
   * away the recovery it already looks like.
   */
  resetKey?: string;
}

interface State {
  error: Error | null;
  /** Where it threw, from React's component stack. Shown beside the message. */
  where?: string;
  resetKey?: string;
}

/**
 * React Error Boundary that wraps a feature module to prevent one
 * failed component from crashing the entire application.
 *
 * Usage:
 * ```tsx
 * <FeatureErrorBoundary>
 *   <Dashboard />
 * </FeatureErrorBoundary>
 * ```
 */
export class FeatureErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { error: null, resetKey: props.resetKey };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  static getDerivedStateFromProps(props: Props, state: State): Partial<State> | null {
    if (props.resetKey === state.resetKey) return null;
    // Cleared during render rather than in an effect: the fallback must not
    // paint once more on the route the user has already navigated to.
    return { error: null, resetKey: props.resetKey };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Log to console in dev; in production this would go to an error
    // reporting service (e.g. Sentry, Logfire).
    console.error('[FeatureErrorBoundary]', error, info.componentStack);
    // And onto the card itself, so a screenshot of the fault names the
    // component — the console is empty by the time anyone thinks to look.
    this.setState({ where: topFrames(info.componentStack ?? undefined) });
  }

  private reset = () => {
    this.setState({ error: null, where: undefined });
  };

  render() {
    const { error } = this.state;
    if (error) {
      if (this.props.fallback) {
        return this.props.fallback(error, this.reset);
      }
      return (
        <ErrorBoundaryFallback
          error={error}
          detail={this.state.where}
          reset={this.reset}
        />
      );
    }
    return this.props.children;
  }
}
